<template>
  <div class="browse-all-categories" :class="{ desktop: $isDesktop }">
    <p v-if="shouldShowBrowseAllLink">
      <pu-search-link
        :hierarchy-path="currentHierarchyPath"
        category="campsites"
        exclude-type="browseAllCategories"
      >
        <translate
          v-if="currentHierarchy"
          :tt="{ hierarchyPath: currentHierarchyName }"
        >
          Browse all {hierarchyPath} campsites
        </translate>
        <translate v-else>Browse all campsite types</translate>
      </pu-search-link>
    </p>

    <template v-if="categories.length">
      <translate v-if="selectedCategoryIds && selectedCategoryIds.length">
        Search instead for:
      </translate>
      <section>
        <pu-search-link
          v-for="category in categories"
          :key="category.id"
          :hierarchy-path="currentHierarchyPath"
          :category="category.rootSlug"
          exclude-type="browseAllCategories"
        >
          <img
            :src="
              categorySvgs[`/assets/images/category-icons/${category.slug}.svg`]
                ?.default
            "
            :alt="`${currentHierarchyName} ${category.name}`"
            width="26"
            height="26"
          />
        </pu-search-link>
      </section>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useNuxtApp } from 'nuxt/app'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { UnifiedCampsiteSearchService } from '~/apps/search/infrastructure/UnifiedCampsiteSearchService'
import { useSearchStoreSetup } from '~/stores/useSearchStore'
import { getFacetRules, shouldRemoveFacets } from '~/utility/facets'
import { GLAMPING_CATEGORIES, isGlamping } from '~/utility/glamping'
import type { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import type { HierarchyTrail } from '../../search/schemas'
import { getCampsiteCategoriesPresenter } from '../presenter-json/CampsiteCategoriesUpdaterJson'

const props = withDefaults(
  defineProps<{
    currentHierarchy?: HierarchyTrail
    selectedCategoryIds?: string[]
  }>(),
  {
    selectedCategoryIds: () => [],
  },
)

const route = useRoute()
const { $isDesktop } = useNuxtApp()
const searchStore = useSearchStoreSetup(usePinia())
const langCode = (route.params.lang as string) || 'en-gb'
const unifiedSearchService = new UnifiedCampsiteSearchService()

const categorySvgs = import.meta.glob<{ default: string }>(
  '@/assets/images/category-icons/*.svg',
  {
    query: '?url',
    eager: true,
  },
)
const categoriesPresenter = getCampsiteCategoriesPresenter(langCode)

onUnmounted(() => {
  if (unifiedSearchService && unifiedSearchService.cancel) {
    unifiedSearchService.cancel()
  }
})

const currentHierarchyName = computed(() => props.currentHierarchy?.name || '')
const currentHierarchyPath = computed(() => props.currentHierarchy?.path || '')
const countThreshold = computed(() => {
  return searchStore.searchFilters.facets?.length ? 5 : 1
})

// Fetch search results without selected categories
const { data } = await useAsyncData(
  `browseAllCategories-${JSON.stringify(searchStore.searchFilters)}`,
  async () => {
    const filtersWithoutCategories = {
      ...searchStore.searchFilters,
      categoryIds: [],
      facets: [],
      limit: 0,
      base: null,
      search_url: null,
      facet_counts_only: 'category',
    }
    try {
      const [rules, results] = await Promise.all([
        // Fetch facet rules if facets exist
        searchStore.searchFilters.facets?.length
          ? Promise.all(
              searchStore.searchFilters.facets.map((facet) =>
                getFacetRules(facet, langCode),
              ),
            ).then((ruleResults) => ruleResults.flat())
          : Promise.resolve([]),
        // Fetch counts
        unifiedSearchService.search(langCode, filtersWithoutCategories),
      ])
      const counts = (results.facetFullBookableCategory ||
        results.categoriesCounts) as Record<string, number> | undefined
      return {
        counts,
        rules,
      }
    } catch (error) {
      // console.error('Error fetching categories search results:', error)
      return {
        counts: {},
        rules: [],
      }
    }
  },
  {
    watch: [searchStore.searchFilters],
    server: true,
    lazy: process.client,
    dedupe: 'cancel',
    getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
  },
)

const categoriesFilteredList = computed((): CampsiteCategory[] => {
  if (!data.value?.counts || Object.keys(data.value?.counts).length === 0) {
    return []
  }
  const counts = data.value?.counts
  const threshold = countThreshold.value
  return categoriesPresenter.list
    .filter((category) => category.id.toString() !== '13')
    .filter((category) => {
      // If counts is null or count doesn't exist, include the category
      if (counts === null || counts === undefined) {
        return false
      }

      const count = counts[category.slug]
      if (count === undefined || count < threshold) {
        return false
      }

      // Check if category should be excluded based on facet rules
      const rules = data.value?.rules
      if (rules?.length) {
        return !rules.some(
          (rule) =>
            rule.category_all ||
            rule.category_ids.includes(Number(category.id)),
        )
      }
      return true
    })
})

const categories = computed(() => {
  const counts = data.value?.counts
  return isGlamping(props.selectedCategoryIds)
    ? categoriesFilteredList.value
    : [
        ...categoriesFilteredList.value.filter(
          (cat) => !props.selectedCategoryIds?.includes(cat.id),
        ),
        ...((!counts || Object.keys(counts).length) &&
        GLAMPING_CATEGORIES.some((id) => {
          const category = categoriesPresenter.byIdMap[id]
          const rules = data.value?.rules
          return (
            category &&
            (!counts || counts[category.slug] >= countThreshold.value) &&
            // Check if glamping should be excluded based on facet rules
            !(rules || []).some(
              (rule) => rule.category_all || rule.category_glamping,
            )
          )
        })
          ? [
              {
                breadcrumbName: '',
                id: 'glamping',
                isByo: false,
                name: 'Glamping',
                order: 0,
                pretty_name: '',
                rootSlug: 'glamping',
                slug: 'glamping',
              },
            ]
          : []),
      ]
})

// Check if "Browse all campsites" link should be shown
// Applies the same rules as breadcrumbs (SearchBreadcrumbs.vue):
// 1. Don't show if facet would be removed (causes redirect)
// 2. Don't show if destination URL has 0 bookable sites
// 3. Don't show if URL would have no results
const shouldShowBrowseAllLink = computed(() => {
  // Rule 1: Must have selected categories to offer "Browse all" alternative
  if (!props.selectedCategoryIds.length) {
    return false
  }

  const counts = data.value?.counts
  const rules = data.value?.rules

  // Rule 2: URL would have no results if no categories have enough count
  // If categories.length === 0, it means all categories were filtered out
  // either because they have count < threshold or are excluded by facet rules
  if (!categories.value.length) {
    return false
  }

  // Rule 3: Check if the "Browse all" destination would have enough total results
  // Calculate total count across all categories (this is the count for the hierarchy without category filter)
  if (counts) {
    const totalCount = Object.values(counts).reduce(
      (sum, count) => sum + (count || 0),
      0,
    )

    if (totalCount < countThreshold.value) {
      return false
    }
  }

  // Rule 4: Facet would be removed (causing redirect)
  // Use the facet removal helper to check if facets would be removed
  // when clicking "Browse all campsites" (which goes to /campsites/ with no specific category)
  const wouldRemoveFacets = shouldRemoveFacets(
    rules || [],
    props.selectedCategoryIds,
    'all-campsites',
  )

  if (wouldRemoveFacets) {
    return false
  }

  return true
})
</script>

<style scoped>
.browse-all-categories {
  padding: 0 1.4em 2em;
  color: var(--colour-dark-blue);
  font-size: var(--body);
  max-width: 100vw;
}

p {
  margin: 0 0 1em 0;
}

p a {
  color: var(--colour-dark-blue);
  text-decoration: underline;
}

section {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin: 0.5em 0;
  gap: 1em;
}

img {
  width: auto;
  height: 2em;
  display: block;
}

.browse-all-categories.desktop {
  padding: 0 1.4em 2em var(--page-width-padding);
}
</style>
